import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Lesson, UserProgress } from "@/types/lesson";

/**
 * Hook to fetch module details for a given path ID
 * @param pathId - The module ID to fetch details for
 * @returns Module details with topics
 */
export const useModuleDetails = (pathId: string) => {
  return useQuery({
    queryKey: ['module-details', pathId],
    queryFn: async () => {
      console.log("🔍 Fetching module details for pathId:", pathId);
      
      const { data, error } = await supabase
        .from('modules')
        .select('*, topics(*)')
        .eq('id', pathId)
        .maybeSingle();
      
      if (error) {
        console.error("❌ Error fetching module details:", error);
        return null;
      }

      console.log("📋 Module details found:", {
        moduleId: data?.id,
        moduleTitle: data?.title,
        topicTitle: data?.topics?.title,
        topicId: data?.topic_id
      });

      return data;
    },
  });
};

/**
 * Hook to fetch lessons for a specific module
 * @param pathId - The module ID to fetch lessons for
 * @returns Array of lessons with enhanced debugging and error handling
 */
export const useLessons = (pathId: string) => {
  return useQuery({
    queryKey: ['lessons', pathId],
    queryFn: async () => {
      console.log("🔍 Fetching lessons for module pathId:", pathId);
      
      // Fetch lessons specifically for this module
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('module_id', pathId)
        .eq('status', 'active')
        .order('order_index');
      
      if (error) {
        console.error("❌ Error fetching lessons for module:", pathId, error);
        throw error;
      }

      console.log("🎯 Raw lessons data fetched:", {
        moduleId: pathId,
        foundLessons: data?.length || 0,
        lessonTitles: data?.map(l => l.title) || [],
        rawData: data
      });

      // Process and parse lessons content with enhanced validation
      const processedLessons = (data || []).map(lesson => {
        console.log("🔄 Processing lesson:", {
          id: lesson.id,
          title: lesson.title,
          order_index: lesson.order_index,
          hasRawContent: !!lesson.content,
          contentLength: lesson.content?.length || 0,
          contentType: typeof lesson.content,
          contentPreview: lesson.content ? 
            (typeof lesson.content === 'string' ? lesson.content.substring(0, 100) + "..." : 
             JSON.stringify(lesson.content).substring(0, 100) + "...") : 
            "No content"
        });

        // Parse JSON content safely with better error handling
        let parsedContent = null;
        if (lesson.content) {
          try {
            if (typeof lesson.content === 'string') {
              // Try to parse the JSON string
              parsedContent = JSON.parse(lesson.content);
              console.log("✅ Successfully parsed lesson content JSON for:", lesson.title);
            } else if (typeof lesson.content === 'object') {
              // Already an object, use directly
              parsedContent = lesson.content;
              console.log("✅ Using lesson content object directly for:", lesson.title);
            }
            
            // Validate the parsed content structure
            if (parsedContent && typeof parsedContent === 'object') {
              console.log("📊 Parsed content structure for", lesson.title, ":", {
                hasSections: !!parsedContent.sections,
                sectionsCount: parsedContent.sections?.length || 0,
                hasObjectives: !!parsedContent.objectives,
                objectivesCount: parsedContent.objectives?.length || 0,
                hasKnowledgeChecks: !!parsedContent.knowledgeChecks,
                knowledgeChecksCount: parsedContent.knowledgeChecks?.length || 0,
                hasRewards: !!parsedContent.rewards
              });
            }
          } catch (parseError) {
            console.warn("⚠️ Failed to parse lesson content for:", lesson.title, parseError);
            console.log("🔍 Raw content that failed to parse:", lesson.content);
            // Keep the raw content for fallback processing
            parsedContent = lesson.content;
          }
        }

        return {
          ...lesson,
          content: parsedContent
        };
      });

      console.log("✅ Final processed lessons for module", pathId, ":", {
        totalLessons: processedLessons.length,
        lessonsWithContent: processedLessons.filter(l => l.content).length,
        lessonsWithValidContent: processedLessons.filter(l => {
          if (!l.content) return false;
          if (typeof l.content === 'object' && l.content.sections) return true;
          if (typeof l.content === 'string' && l.content.length > 0) return true;
          return false;
        }).length,
        processedData: processedLessons.map(l => ({
          id: l.id,
          title: l.title,
          hasContent: !!l.content,
          contentType: typeof l.content,
          contentValid: !!(l.content && (
            (typeof l.content === 'object' && l.content.sections) ||
            (typeof l.content === 'string' && l.content.length > 0)
          ))
        }))
      });
      
      return processedLessons as Lesson[];
    },
    // Add retry logic for better reliability
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to fetch user progress data
 * @param pathId - The module ID (used for logging context)
 * @returns Array of user progress records
 */
export const useUserProgress = (pathId: string) => {
  return useQuery({
    queryKey: ['userProgress', pathId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log("👤 No user session found for progress tracking");
        return [];
      }

      console.log("📈 Fetching progress for user:", session.user.id);
      
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', session.user.id);

      if (error) {
        console.error("❌ Error fetching user progress:", error);
        throw error;
      }

      console.log("📊 User progress data found:", {
        totalProgress: data?.length || 0,
        progressDetails: data?.map(p => ({
          lessonId: p.lesson_id,
          status: p.status,
          completedAt: p.completed_at,
          points: p.points
        })) || []
      });
      
      return data as UserProgress[];
    },
    staleTime: 0, // Always refetch to ensure fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });
};
